import orderJoinHelper from "../../order-join.helper";
import OrderJoinOrderDetailsPassenger from "../types/order-join-order-details-passenger";
import OrderJoinOrderDetails from "../types/order-join-order-details";
import orderJoinOrderDetailsWaypointFactory from "./order-join-order-details-waypoint.factory";
import { SearchRoadRoutingResponseRouteLeg } from "../../../../../common/utils/search-road-route/search-road-routing.response";
import OrderJoinOrderDetailsDriver, {
  OrderJoinOrderDetailsDriverRole,
} from "../types/order-join-order-details-driver";
import { RideSolvedResponseRideSolvedWaypoint } from "../../../../../common/api/raily/ride/solved/ride-solved.response";
import OrderJoin, {
  OrderJoinExternalCargoItemSource,
  OrderJoinPreviewDriver,
  OrderJoinTransportingOrderCreatePlanEntry,
} from "../../../../../common/services/order/join/order-join";
import SourceType from "../../../../../common/types/source-type";

const createExternalPassengersList = (
  responseData: OrderJoin,
): OrderJoinOrderDetailsPassenger[] => {
  const externalPassengersList: OrderJoinOrderDetailsPassenger[] = [];

  const externalSubjects = responseData.cargoOrder.cargoItemsSourceList.filter(
    (sourceItem) =>
      sourceItem.cargoItemSource.sourceType === SourceType.EXTERNAL,
  );

  if (externalSubjects.length) {
    externalSubjects.forEach((subject) => {
      const subjectId = subject.cargoSubjectId;
      const subjectData =
        subject.cargoItemSource as OrderJoinExternalCargoItemSource;

      const externalPassenger: OrderJoinOrderDetailsPassenger = {
        uuid: subjectId,
        name: `${subjectData.firstName} ${subjectData.lastName}`,
      };

      externalPassengersList.push(externalPassenger);
    });
  }

  return externalPassengersList;
};

const createInternalPassengersList = (
  responseData: OrderJoin,
): OrderJoinOrderDetailsPassenger[] => {
  return responseData.passengers.map((passenger) => ({
    uuid: passenger.id,
    name: `${passenger.firstName} ${passenger.lastName}`,
  }));
};

const createPassengers = (
  responseData: OrderJoin,
): OrderJoinOrderDetailsPassenger[] => {
  const externalPassengersList = createExternalPassengersList(responseData);
  const internalPassengersList = createInternalPassengersList(responseData);

  return [...internalPassengersList, ...externalPassengersList];
};

const createAssignedDriver = (
  responseDriver: OrderJoinPreviewDriver,
): OrderJoinOrderDetailsDriver => {
  return {
    uuid: responseDriver.id,
    firstName: responseDriver.firstName,
    lastName: responseDriver.lastName,
    displayName: `${responseDriver.firstName} ${responseDriver.lastName}`,
    phoneNumber: `${responseDriver.mobilePrimaryPrefix} ${responseDriver.mobilePrimary}`,
    role: OrderJoinOrderDetailsDriverRole.ASSIGNED,
  };
};

const createCandidateDriver = (
  responseCandidateDriver: OrderJoinTransportingOrderCreatePlanEntry["driver"],
): OrderJoinOrderDetailsDriver => {
  return {
    uuid: responseCandidateDriver.id,
    firstName: responseCandidateDriver.firstName,
    lastName: responseCandidateDriver.lastName,
    displayName: `${responseCandidateDriver.firstName} ${responseCandidateDriver.lastName}`,
    phoneNumber: `${responseCandidateDriver.mobilePrimaryPrefix} ${responseCandidateDriver.mobilePrimary}`,
    role: OrderJoinOrderDetailsDriverRole.CANDIDATE,
  };
};

const createDrivers = (
  responseDrivers: OrderJoinPreviewDriver[],
  responseCandidateDrivers: OrderJoinTransportingOrderCreatePlanEntry["driver"][],
) => {
  const assignedDrivers = responseDrivers.map(createAssignedDriver);
  const candidateDrivers = responseCandidateDrivers.map(createCandidateDriver);

  const drivers: OrderJoinOrderDetailsDriver[] = [
    ...assignedDrivers,
    ...candidateDrivers,
  ];

  return drivers;
};

const createOrderDetails = (
  data: OrderJoin,
  responseCargoOrderSolvedRides: RideSolvedResponseRideSolvedWaypoint[] | null,
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
): OrderJoinOrderDetails => {
  const isOrderSolved = orderJoinHelper.isOrderSolved(
    data.cargoOrder.transportingOrders ?? [],
  );

  const responseCandidateDrivers = data.cargoOrder.transportingOrders
    .map((transportingOrder) =>
      transportingOrder.createPlanEntryEntries.map((p) => p.driver),
    )
    .flat();

  const result = {
    uuid: data.cargoOrder.id,
    routeWaypoints: isOrderSolved
      ? orderJoinOrderDetailsWaypointFactory.createWaypointsForSolvedRoute(
          data.cargoOrder.transportingOrders,
          responseRouteLegs,
        )
      : orderJoinOrderDetailsWaypointFactory.createWaypointsForOrderedRoute(
          data.cargoOrder.ride,
          responseRouteLegs,
          responseCargoOrderSolvedRides ?? [],
        ),
    passengers: createPassengers(data),
    drivers: createDrivers(data.drivers, responseCandidateDrivers),
    orderId: data.cargoOrder.humanId,
    isOrderSolved: isOrderSolved,
    nodeExclusion: {
      excludeApproaching: data.nodeExclusion.excludeApproaching,
      excludeInside: data.nodeExclusion.excludeInside,
      excludeReturning: data.nodeExclusion.excludeReturning,
    },
  };

  return result;
};

const orderJoinOrderDetailsFactory = {
  createOrderDetails,
};

export default orderJoinOrderDetailsFactory;
